import React, { useState } from "react";

import { Field, Label, Description } from "V2.0/common/fieldset";
import { Button } from "V2.0/common/button";
import { MultiListbox } from "V2.0/components/MultiListBox";

import { buildContextFilterOptions } from "../models/contextFilter";
import { showsSequencing } from "../models/sections";

type Props = {
  triggerEvent: string;
  currentId: string | undefined;
  allAPIs: any[];
  contextCharacteristics: any[];
  contextFilter: string[];
  sequenceAPIIds: string[];
  activationAPIIds: string[];
  onChange: (patch: { contextFilter?: string[]; sequenceAPIIds?: string[]; activationAPIIds?: string[] }) => void;
};

export function Advanced({ triggerEvent, currentId, allAPIs, contextCharacteristics, contextFilter, sequenceAPIIds, activationAPIIds, onChange }: Props) {
  const [expanded, setExpanded] = useState(false);
  const sequencing = showsSequencing(triggerEvent);

  const setCount = contextFilter.length + (sequencing ? sequenceAPIIds.length + activationAPIIds.length : 0);

  const siblings = (allAPIs ?? []).filter((api) => api._id && api.triggerEvent === "duringCall" && api._id !== currentId);

  const isFollowUpOfAnotherAPI = (allAPIs ?? []).some(
    (api) => api._id !== currentId && Array.isArray(api.sequenceAPIIds) && api.sequenceAPIIds.includes(currentId)
  );

  const followUpOptions = siblings
    .filter((api) => !(Array.isArray(api.sequenceAPIIds) && api.sequenceAPIIds.length > 0) && !activationAPIIds.includes(api._id))
    .map((api) => ({
      id: api._id,
      value: api._id,
      label: Array.isArray(api.activationAPIIds) && api.activationAPIIds.length > 0 ? `${api.apiName} (conditional)` : api.apiName,
    }));

  const activationOptions = siblings
    .filter((api) => !sequenceAPIIds.includes(api._id))
    .map((api) => ({ id: api._id, value: api._id, label: api.apiName }));

  const hasContextOptions = (contextCharacteristics ?? []).length > 0;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-zinc-500">{setCount === 0 ? "Nothing set" : `${setCount} set`}</p>
        <Button outline onClick={() => setExpanded((value) => !value)}>
          {expanded ? "Hide options" : "Show options"}
        </Button>
      </div>

      {expanded && (
        <>
          <Field>
            <Label>Context filter</Label>
            <Description>
              {hasContextOptions
                ? "Considered only when, for every characteristic you pick from, at least one selected value is currently active on the call. Leave empty to always consider it."
                : "Set up characteristics under Context Management to filter this API by what the call is about."}
            </Description>
            {hasContextOptions && (
              <MultiListbox
                className="my-2"
                options={buildContextFilterOptions(contextCharacteristics, contextFilter)}
                value={contextFilter}
                onChange={(value) => onChange({ contextFilter: value })}
              />
            )}
          </Field>

          {sequencing && !isFollowUpOfAnotherAPI && (
            <Field>
              <Label>Then run other APIs</Label>
              <Description>
                After this API succeeds, the selected APIs run automatically in the background, in the order selected. APIs chosen here stop
                running on their own checks, and their spoken messages are not used. Follow-ups marked (conditional) are skipped until their
                own "Available only after" condition is met.
              </Description>
              {followUpOptions.length > 0 ? (
                <MultiListbox className="my-2" options={followUpOptions} value={sequenceAPIIds} onChange={(value) => onChange({ sequenceAPIIds: value })} />
              ) : (
                <p className="mt-2 text-sm text-zinc-500">No other during-call API can follow this one yet.</p>
              )}
            </Field>
          )}

          {sequencing && (
            <Field>
              <Label>Available only after another API</Label>
              <Description>
                {isFollowUpOfAnotherAPI
                  ? "This API runs as a follow-up of another API. If you select APIs here, it is skipped in that sequence until any of them has completed successfully in the call."
                  : "This API is considered only after any of the selected APIs has completed successfully in the call. Leave empty to keep it always available."}
              </Description>
              {activationOptions.length > 0 ? (
                <MultiListbox className="my-2" options={activationOptions} value={activationAPIIds} onChange={(value) => onChange({ activationAPIIds: value })} />
              ) : (
                <p className="mt-2 text-sm text-zinc-500">No other during-call API to wait for yet.</p>
              )}
            </Field>
          )}
        </>
      )}
    </div>
  );
}
